/**
 * The plane API over HTTP (`/api/*` on the backend).
 *
 * Every Dash response is `{ meta, data, error }`: unwrapping happens once, here. A failed call
 * throws an {@link ApiError}; a 401 is also reported to the session module, which tells the shell
 * the session is gone.
 */
import { connectivity } from '../../lib/connectivity/index.js';
import { session } from '../../lib/session/index.js';
import { ApiError } from './index.js';

async function call(method, path, body) {
  let response;
  try {
    response = await fetch(path, {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // No response at all: the network, not the backend (see the connectivity module).
    connectivity.reportNetworkFailure();
    throw error;
  }
  connectivity.reportNetworkSuccess();

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // a body-less response, or an HTML error page: the status still says enough
  }

  if (!response.ok) {
    if (response.status === 401) {
      session.reportUnauthorized();
    }
    let message = payload?.error?.detail ?? payload?.error?.title;
    if (response.status === 503 && payload === null && response.headers.has('Retry-After')) {
      // not the backend: hop's page while the plane is suspended (hop app suspend), until resumed
      message = 'plane is temporarily suspended for maintenance: try again later';
    } else if (response.status >= 500) {
      // a failure of the backend, not a refusal: what to look for in its log (Dash logs the path)
      message = `${message ?? 'The backend failed'} (HTTP ${response.status} on ${method} ${path})`;
    }
    throw new ApiError(response.status, payload?.error?.code, message);
  }
  return payload?.data ?? null;
}

const segment = encodeURIComponent;

export const httpApi = {
  /**
   * `{ username, admin, operator, customers, memberships }`, `memberships` being
   * `{ id, name, role }`; 401 for a visitor who is not signed in.
   */
  session: () => call('GET', '/api/session'),

  servers: () => call('GET', '/api/servers'),
  /** A server and the instances assigned to it (`instances`). */
  server: (id) => call('GET', `/api/servers/${segment(id)}`),
  /** Operators only: adds a server; the answer is its first enrollment token, shown this once. */
  createServer: (id, name) => call('POST', '/api/servers', { id, name }),
  /** Operators only: a new enrollment token for the server; the unused previous one stops working. */
  newEnrollmentToken: (id) => call('POST', `/api/servers/${segment(id)}/enrollment-tokens`),
  /** Operators only: 'revoked' closes the connection of the server at once; 'active' lets it connect again. */
  setServerStatus: (id, status) => call('PUT', `/api/servers/${segment(id)}/status`, { status }),
  /**
   * Operators only: revokes the key the server last connected with. The server stays active; its
   * clawlet must enroll again, with a new token, to connect.
   */
  revokeServerKey: (id) => call('POST', `/api/servers/${segment(id)}/revoke-key`),
  /**
   * Operators only: takes over a held server, whose clawlet is ahead of the plane: the desired state
   * of the plane replaces the clawlet's, and the instances the plane does not assign to the server
   * are removed from it. The answer is the server.
   */
  takeOverServer: (id) => call('POST', `/api/servers/${segment(id)}/take-over`),
  /**
   * Operators only: the networks (CIDR blocks or addresses) the server is expected to connect
   * from; an enrollment request from elsewhere is marked. The answer is the server.
   */
  setExpectedNetworks: (id, networks) => call('PUT', `/api/servers/${segment(id)}/expected-networks`, { networks }),

  /** Operators only: the clawlets waiting for an operator to approve their enrollment request, oldest first. */
  enrollmentRequests: () => call('GET', '/api/enrollment-requests'),
  /**
   * Operators only: approves a request for `serverId` (null: the one the clawlet asked for),
   * created with `name` if it does not exist; `rememberAddress` adds the address of the request to
   * its expected networks. The clawlet gets its certificate at its next request; the previous
   * keys of the server are revoked. The answer is the server.
   */
  approveEnrollmentRequest: (id, { serverId = null, name = null, rememberAddress = false } = {}) =>
    call('POST', `/api/enrollment-requests/${segment(id)}/approve`, { serverId, name, rememberAddress }),
  /** Operators only: denies a request, whose key will never be approved; the answer is the requests still pending. */
  denyEnrollmentRequest: (id) => call('POST', `/api/enrollment-requests/${segment(id)}/deny`),

  /**
   * Operators only: adds an instance to the server, optionally for a customer (`customerId`).
   * `name` is its id, the slug of its gateway; `displayName` the name of the workspace (without
   * one, the id names it).
   * Without a template it has no revision yet: nothing runs until the first one is applied. From a
   * template (`templateId`, the image version is then the template's) the template is its
   * revision 1, applied at once: one assistant, with the id `assistantId`, or the one the template
   * proposes when null.
   */
  addInstance: (serverId, { name, displayName = null, imageVersion = null, customerId = null, templateId = null, assistantId = null }) =>
    call('POST', `/api/servers/${segment(serverId)}/instances`, { name, displayName, imageVersion, customerId, templateId, assistantId }),

  /** Operators only: the templates in use, by name; `archived` lists the archived ones too. */
  templates: ({ archived = false } = {}) => call('GET', `/api/templates${archived ? '?archived=true' : ''}`),
  /** Operators only: a template with its params and persona. */
  template: (id) => call('GET', `/api/templates/${segment(id)}`),
  /**
   * Operators only: `{ name, description, imageVersion, assistantId, params, persona, operatorRules }`, plus `fromInstance` to take
   * the operator's part (image version, model, thinking, operator rules) from the desired revision of that instance.
   */
  createTemplate: (content) => call('POST', '/api/templates', content),
  /**
   * Operators only: `content` carries the `version` it was made on (409 if the template changed since). Its version
   * grows by one; the instances made from it do not change.
   */
  updateTemplate: (id, content) => call('PUT', `/api/templates/${segment(id)}`, content),
  /**
   * Operators only: brings the instances made from an older version of the template to the current one (model,
   * thinking, operator rules, image version; never what the customer wrote). The answer is
   * `{ version, updated, unchanged, skipped }`, `skipped` being `{ instance, reason }`.
   */
  updateTemplateInstances: (id) => call('POST', `/api/templates/${segment(id)}/instance-updates`),
  /** Operators only: an archived template creates no new instance. */
  archiveTemplate: (id, archived) => call('PUT', `/api/templates/${segment(id)}/archived`, { archived }),

  instances: () => call('GET', '/api/instances'),
  instance: (name) => call('GET', `/api/instances/${segment(name)}`),
  revision: (name, revisionId) => call('GET', `/api/instances/${segment(name)}/revisions/${segment(revisionId)}`),

  /**
   * Operators only: one more assistant from a template (`templateId`), with the id `assistantId`
   * or, when null, the one the template proposes; in a new revision applied at once.
   */
  addAssistantFromTemplate: (name, { templateId, assistantId = null }) => call('POST', `/api/instances/${segment(name)}/assistants`, { templateId, assistantId }),

  /**
   * "Save": a new draft revision. Nothing reaches the server until it is applied. `content.templates`,
   * operators only, is the template each new assistant starts from, by assistant id: once the
   * revision is applied the assistant came from it, and an update of the template reaches it.
   */
  saveRevision: (name, content) => call('POST', `/api/instances/${segment(name)}/revisions`, content),

  /**
   * "Apply" and "Rollback": makes a revision the desired one, publishing it if it is a draft. The
   * answer is the new desired state, not an outcome: that comes later, reported by the clawlet.
   */
  apply: (name, revisionId) => call('PUT', `/api/instances/${segment(name)}/desired-revision`, { revisionId }),

  /**
   * Operators only: removes the instance from its server (no desired revision). The clawlet keeps
   * its state volume; applying a revision deploys it again.
   */
  removeInstance: (name) => call('DELETE', `/api/instances/${segment(name)}/desired-revision`),

  /**
   * Operators only: deletes an instance that was never deployed (no published revision), with its
   * drafts, and frees its name. A 409 for one that was deployed: remove it from its server instead.
   */
  deleteInstance: (name) => call('DELETE', `/api/instances/${segment(name)}`),

  /** Renames the workspace (owners of its customer and operators): the name of the instance stays. */
  renameWorkspace: (name, displayName) => call('PUT', `/api/instances/${segment(name)}/display-name`, { displayName }),

  /** Asks for a restart without changing the revision (restartNonce). */
  restart: (name) => call('POST', `/api/instances/${segment(name)}/restart`),

  /**
   * "Allow" on a pairing request (owners and operators): the sender joins the allowed users of the
   * assistant on the channel of the request, in a new revision applied at once.
   */
  allowPairingRequest: (name, assistantId, channel, senderId) =>
    call('POST', `/api/instances/${segment(name)}/assistants/${segment(assistantId)}/pairing-requests/${segment(channel)}/${segment(senderId)}/allow`),
  /** "Ignore" on a pairing request: hidden until it expires, after an hour. */
  ignorePairingRequest: (name, assistantId, channel, senderId) =>
    call('DELETE', `/api/instances/${segment(name)}/assistants/${segment(assistantId)}/pairing-requests/${segment(channel)}/${segment(senderId)}`),

  /** Write-only: the value goes to the secret store and is never read back. */
  setSecret: (name, secretName, value) => call('PUT', `/api/instances/${segment(name)}/secrets/${segment(secretName)}`, { value }),

  /** Operators only: the latest apply events, of one instance or of all of them. */
  events: ({ instance, limit = 20 } = {}) => {
    const query = new URLSearchParams({ limit: String(limit) });
    if (instance) {
      query.set('instance', instance);
    }
    return call('GET', `/api/apply-events?${query}`);
  },

  imageVersion: (version) => call('GET', `/api/image-versions/${segment(version)}`),

  /** Operators only: customers, their members (`{ username, role }`) and their instances. */
  customers: () => call('GET', '/api/customers'),
  createCustomer: (name) => call('POST', '/api/customers', { name }),
  /** The members of a customer: for its members, its owners and operators. */
  customerMembers: (customerId) => call('GET', `/api/customers/${segment(customerId)}/members`),
  /**
   * Operators only: adds a Dash user, by name, as `member` or `owner`; for a user already in the
   * customer, changes their role. Owners invite instead.
   */
  addCustomerUser: (customerId, username, role = 'member') =>
    call('PUT', `/api/customers/${segment(customerId)}/users/${segment(username)}`, { role }),
  /** Owners and operators. */
  changeCustomerUserRole: (customerId, username, role) =>
    call('PUT', `/api/customers/${segment(customerId)}/users/${segment(username)}/role`, { role }),
  /** Owners and operators; a 409 keeps the last owner of a customer, unless an operator removes them. */
  removeCustomerUser: (customerId, username) => call('DELETE', `/api/customers/${segment(customerId)}/users/${segment(username)}`),
  /**
   * Owners and operators: a link inviting somebody to the customer, which works once and expires.
   * The answer (`{ id, role, url, expiresAt, emailSent }`) is the only place the link appears;
   * `url` is null when the link went by email only (an email given, Dash invitation mail on).
   */
  inviteToCustomer: (customerId, { role = 'member', email = null } = {}) =>
    call('POST', `/api/customers/${segment(customerId)}/invitations`, { role, email }),
  /** Owners and operators: the invitations neither accepted nor revoked, newest first. */
  customerInvitations: (customerId) => call('GET', `/api/customers/${segment(customerId)}/invitations`),
  revokeCustomerInvitation: (customerId, invitationId) =>
    call('DELETE', `/api/customers/${segment(customerId)}/invitations/${segment(invitationId)}`),
  /** Operators only: who the instance belongs to; null takes it away from its customer. */
  assignCustomer: (name, customerId) => call('PUT', `/api/instances/${segment(name)}/customer`, { customerId }),
};
